// /app/frontend/src/components/admin/QuoteRequestsTab.jsx
// Admin management for customer quote requests from the product configurator

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Search, Filter, Phone, Mail, Building2,
  User, Calendar, MapPin, Eye, UserPlus, Loader2,
  Palette, Download, Printer, Trash2, AlertTriangle, Flag, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { takeAdminParam } from '../../lib/adminLinks';
import { isWaitingConfigurator } from '../../lib/waitingRequests';
import { HelpLink } from './HelpLink';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { LeadSourceChip } from './ConsultationSource';
import { LinkedQuoteAction, salesQuoteHref } from './LinkedQuoteAction';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-green-100 text-green-700',
  closed: 'bg-neutral-100 text-neutral-700',
};

const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  quoted: 'Quoted',
  closed: 'Closed',
};

const PRIORITY_COLORS = {
  normal: 'text-neutral-400',
  high: 'text-amber-500',
  urgent: 'text-red-500',
};

const TIMELINE_LABELS = {
  asap: 'As soon as possible',
  within_month: 'Within a month',
  '1_3_months': '1-3 months',
  '3_plus_months': '3+ months',
  just_browsing: 'Just browsing',
};

export const QuoteRequestsTab = ({ token, onStatusUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [waitingOnly, setWaitingOnly] = useState(() => takeAdminParam('filter') === 'unlinked');
  const openRequestId = useRef(takeAdminParam('request'));
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [installers, setInstallers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    quoted: 0,
    closed: 0,
    urgent: 0
  });

  // Fetch quote requests
  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/quote-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
      const target = openRequestId.current && response.data.find((r) => r.id === openRequestId.current);
      if (target) { openRequestId.current = null; setSelectedRequest(target); setDetailModalOpen(true); }
      
      // Calculate stats
      const newStats = {
        total: response.data.length,
        new: response.data.filter(r => r.status === 'new').length,
        contacted: response.data.filter(r => r.status === 'contacted').length,
        quoted: response.data.filter(r => r.status === 'quoted').length,
        closed: response.data.filter(r => r.status === 'closed').length,
        urgent: response.data.filter(r => r.priority === 'urgent').length,
      };
      setStats(newStats);
    } catch (error) {
      console.error('Failed to fetch quote requests:', error);
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch installers for assignment
  const fetchInstallers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data);
    } catch (error) {
      console.error('Failed to fetch installers:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
    fetchInstallers();
  }, [fetchRequests, fetchInstallers]);

  // Filter requests
  const filteredRequests = requests.filter(request => {
    const matchesSearch = !searchTerm || 
      request.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.request_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || request.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority && (!waitingOnly || isWaitingConfigurator(request));
  });
  const waitingCount = requests.filter(isWaitingConfigurator).length;

  // Update request status
  const updateRequestStatus = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/admin/quote-requests/${requestId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => ({ ...prev, status: newStatus }));
      }
      // Notify parent to refresh badge count
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Update request priority
  const updateRequestPriority = async (requestId, newPriority) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/admin/quote-requests/${requestId}`, {
        priority: newPriority
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Priority set to ${newPriority}`);
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (error) {
      toast.error('Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  // Delete quote request
  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this quote request? This action cannot be undone.')) {
      return;
    }
    setDeletingId(requestId);
    try {
      await axios.delete(`${API_URL}/admin/quote-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote request deleted');
      fetchRequests();
      setDetailModalOpen(false);
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      toast.error('Failed to delete quote request');
    } finally {
      setDeletingId(null);
    }
  };

  // Download quote request as PDF
  const downloadPDF = async (request) => {
    setDownloadingId(request.id);
    try {
      const response = await axios.get(`${API_URL}/admin/quote-requests/${request.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-request-${request.request_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  // Print quote request
  const printRequest = (request) => {
    const printWindow = window.open('', '_blank');
    const items = request.items || [];
    
    const itemsHtml = items.map((item, i) => {
      const config = item.config || {};
      return `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${config.room_label || config.room_name || `Item ${i + 1}`}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.color_name || 'N/A'}<br><small>${item.brand_name || ''} ${item.collection_name || ''}</small></td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: center;">${config.width || 'TBD'}" × ${config.height || 'TBD'}"</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${(config.mount_type || 'inside').replace('_', ' ')}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${(config.control_type || 'cordless').replace('_', ' ')}</td>
        </tr>
      `;
    }).join('');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Quote Request ${request.request_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #1a1a1a; border-bottom: 2px solid #f59e0b; padding-bottom: 10px; }
            .section { margin: 20px 0; }
            .section h3 { color: #666; font-size: 14px; text-transform: uppercase; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #f5f5f5; padding: 10px; text-align: left; border-bottom: 2px solid #ddd; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .info-box { background: #f9f9f9; padding: 15px; border-radius: 5px; }
            .priority-urgent { color: red; font-weight: bold; }
            .priority-high { color: orange; font-weight: bold; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>Quote Request ${request.request_number}</h1>
          <p>Submitted: ${formatDate(request.created_at)} ${request.priority === 'urgent' ? '<span class="priority-urgent">⚠️ URGENT</span>' : request.priority === 'high' ? '<span class="priority-high">⭐ HIGH PRIORITY</span>' : ''}</p>
          
          <div class="info-grid">
            <div class="info-box">
              <h3>Customer Information</h3>
              <p><strong>Name:</strong> ${request.customer_name}</p>
              <p><strong>Email:</strong> ${request.customer_email}</p>
              <p><strong>Phone:</strong> ${request.customer_phone || 'Not provided'}</p>
              <p><strong>Company:</strong> ${request.company_name || 'N/A'}</p>
            </div>
            <div class="info-box">
              <h3>Address</h3>
              <p>${request.address || 'Not provided'}</p>
              <p>${request.city || ''} ${request.state || ''} ${request.zip_code || ''}</p>
              <p><strong>Timeline:</strong> ${TIMELINE_LABELS[request.project_timeline] || request.project_timeline}</p>
            </div>
          </div>
          
          <div class="section">
            <h3>Requested Items (${items.length})</h3>
            <table>
              <thead>
                <tr>
                  <th>Window/Room</th>
                  <th>Fabric</th>
                  <th>Size</th>
                  <th>Mount</th>
                  <th>Control</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>
          </div>
          
          ${request.notes ? `<div class="section"><h3>Notes</h3><p>${request.notes}</p></div>` : ''}
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Assign installer to request
  const assignInstaller = async (requestId, installerId) => {
    setUpdating(true);
    try {
      const installer = installers.find(i => i.id === installerId);
      await axios.put(`${API_URL}/admin/quote-requests/${requestId}`, {
        assigned_to: installerId,
        assigned_name: installer?.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Installer assigned');
      fetchRequests();
      setAssignModalOpen(false);
    } catch (error) {
      toast.error('Failed to assign installer');
    } finally {
      setUpdating(false);
    }
  };

  // View request details
  const viewRequest = (request) => {
    setSelectedRequest(request);
    setDetailModalOpen(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-900">Quote Requests</h2>
          <p className="text-neutral-500 text-sm">Manage customer quote requests from the configurator</p>
        </div>
        <div className="flex gap-3">
          {Object.entries(stats).filter(([key]) => key !== 'total').map(([status, count]) => (
            <div 
              key={status}
              className={`px-3 py-2 rounded-lg text-center min-w-[80px] ${STATUS_COLORS[status] || 'bg-neutral-100'}`}
            >
              <div className="text-xl font-bold">{count}</div>
              <div className="text-xs capitalize">{status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by name, email, or request number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[160px]">
            <Flag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">🔴 Urgent</SelectItem>
            <SelectItem value="high">🟡 High</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={waitingOnly ? 'default' : 'outline'} onClick={() => setWaitingOnly(!waitingOnly)} className={waitingOnly ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''} data-testid="quote-requests-filter-unlinked">
          No Sales Quote yet ({waitingCount})
        </Button>
        <HelpLink topic="waiting-for-quote" />
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase w-8">Pri</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Request #</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Items</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Timeline</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Date</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-neutral-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No quote requests found</p>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <motion.tr
                    key={request.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`hover:bg-neutral-50 transition-colors ${request.priority === 'urgent' ? 'bg-red-50' : request.priority === 'high' ? 'bg-amber-50/50' : ''}`}
                  >
                    <td className="px-3 py-3">
                      <Select
                        value={request.priority || 'normal'}
                        onValueChange={(value) => updateRequestPriority(request.id, value)}
                      >
                        <SelectTrigger className="w-10 h-8 p-0 border-0 bg-transparent">
                          {request.priority === 'urgent' ? (
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          ) : request.priority === 'high' ? (
                            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                          ) : (
                            <Flag className="w-4 h-4 text-neutral-300" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">🔴 Urgent</SelectItem>
                          <SelectItem value="high">🟡 High Priority</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-sm text-amber-700">{request.request_number}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-medium text-neutral-900">{request.customer_name}</p>
                        <p className="text-sm text-neutral-500">{request.customer_email}</p>
                        <LeadSourceChip c={request} testPrefix="quote-request-source" />
                        {request.linked_quote_id && <a href={salesQuoteHref(request.linked_quote_id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-1 ml-2 inline-block text-xs text-green-700 underline" data-testid={`linked-quote-badge-${request.id}`}>Sales Quote #{request.linked_quote_id.slice(0, 8).toUpperCase()} · {(request.linked_quote?.status || '').replace(/_/g, ' ')}</a>}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <Palette className="w-4 h-4 text-neutral-400" />
                        <span className="text-sm">{request.item_count || request.items?.length || 0}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm text-neutral-600">
                        {TIMELINE_LABELS[request.project_timeline] || request.project_timeline}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <Select
                        value={request.status}
                        onValueChange={(value) => updateRequestStatus(request.id, value)}
                        disabled={updating}
                      >
                        <SelectTrigger className={`h-8 w-28 text-xs ${STATUS_COLORS[request.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="quoted">Quoted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm text-neutral-500">
                        {formatDate(request.created_at)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewRequest(request)}
                          title="View Details"
                          data-testid={`view-quote-request-${request.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadPDF(request)}
                          disabled={downloadingId === request.id}
                          title="Download PDF"
                        >
                          {downloadingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => printRequest(request)}
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRequest(request.id)}
                          disabled={deletingId === request.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          {deletingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Quote Request {selectedRequest?.request_number}
            </DialogTitle>
            <DialogDescription>
              Submitted on {formatDate(selectedRequest?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6 py-4">
              {/* Status & Assignment */}
              <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedRequest.status]}`}>
                    {STATUS_LABELS[selectedRequest.status]}
                  </span>
                  <Select
                    value={selectedRequest.status}
                    onValueChange={(value) => updateRequestStatus(selectedRequest.id, value)}
                    disabled={updating}
                  >
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue placeholder="Update status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  {selectedRequest.assigned_to ? (
                    <span className="text-sm text-neutral-600">
                      Assigned to: <strong>{installers.find(i => i.id === selectedRequest.assigned_to)?.name}</strong>
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssignModalOpen(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Assign
                    </Button>
                  )}
                </div>
              </div>

              <LinkedQuoteAction request={selectedRequest} token={token} onLinked={(patch) => {
                setSelectedRequest((prev) => ({ ...prev, ...patch }));
                setRequests((prev) => prev.map((r) => (r.id === selectedRequest.id ? { ...r, ...patch } : r)));
              }} />

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-medium text-neutral-900 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer Info
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {selectedRequest.customer_name}</p>
                    <p className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {selectedRequest.customer_email}
                    </p>
                    {selectedRequest.customer_phone && (
                      <p className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {selectedRequest.customer_phone}
                      </p>
                    )}
                    {selectedRequest.company_name && (
                      <p className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {selectedRequest.company_name}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-neutral-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Address
                  </h4>
                  <div className="text-sm text-neutral-600">
                    {selectedRequest.address ? (
                      <>
                        <p>{selectedRequest.address}</p>
                        <p>{selectedRequest.city}, {selectedRequest.state} {selectedRequest.zip_code}</p>
                      </>
                    ) : (
                      <p className="text-neutral-400">Not provided</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="space-y-3">
                <h4 className="font-medium text-neutral-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Project Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-500">Timeline:</span>{' '}
                    <span className="font-medium">{TIMELINE_LABELS[selectedRequest.project_timeline] || selectedRequest.project_timeline}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Preferred Contact:</span>{' '}
                    <span className="font-medium capitalize">{selectedRequest.preferred_contact}</span>
                  </div>
                </div>
                {selectedRequest.notes && (
                  <div className="mt-2">
                    <span className="text-neutral-500 text-sm">Notes:</span>
                    <p className="mt-1 text-sm bg-neutral-50 p-3 rounded-lg">{selectedRequest.notes}</p>
                  </div>
                )}
              </div>

              {/* Requested Items */}
              <div className="space-y-3">
                <h4 className="font-medium text-neutral-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Requested Items ({selectedRequest.items?.length || 0})
                </h4>
                <div className="grid gap-3">
                  {selectedRequest.items?.map((item, index) => {
                    const config = item.config || {};
                    return (
                      <div key={item.id || item.color_id || `req-item-${index}`} className="flex items-start gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                        {item.visualization_url ? (
                          <img 
                            src={item.visualization_url}
                            alt={item.color_name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        ) : (
                          <div 
                            className="w-20 h-20 rounded-lg border border-neutral-200 flex items-center justify-center"
                            style={{ backgroundColor: item.color_hex || '#ccc' }}
                          >
                            <span className="text-white text-xs font-medium drop-shadow">{item.color_name?.slice(0,2)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-neutral-900">
                                {config.room_label || config.room_name || `Window ${index + 1}`}
                              </p>
                              <p className="text-sm text-amber-700 font-medium">{item.color_name}</p>
                            </div>
                            {item.product_type && (
                              <span className="text-xs bg-neutral-200 px-2 py-1 rounded">
                                {item.product_type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-neutral-500 mt-1">
                            {item.brand_name} → {item.collection_name}
                            {item.material_name && ` → ${item.material_name}`}
                          </p>
                          
                          {/* Size and Options */}
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div className="bg-white px-2 py-1 rounded border">
                              <span className="text-neutral-400">Size:</span>{' '}
                              <span className="font-medium">
                                {config.width || 'TBD'}" × {config.height || 'TBD'}"
                              </span>
                            </div>
                            <div className="bg-white px-2 py-1 rounded border">
                              <span className="text-neutral-400">Mount:</span>{' '}
                              <span className="font-medium capitalize">{config.mount_type || 'Inside'}</span>
                            </div>
                            <div className="bg-white px-2 py-1 rounded border">
                              <span className="text-neutral-400">Control:</span>{' '}
                              <span className="font-medium capitalize">{(config.control_type || 'Cordless').replace('-', ' ')}</span>
                            </div>
                            {config.quantity > 1 && (
                              <div className="bg-white px-2 py-1 rounded border">
                                <span className="text-neutral-400">Qty:</span>{' '}
                                <span className="font-medium">{config.quantity}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                onClick={() => printRequest(selectedRequest)}
                className="flex-1 sm:flex-none"
              >
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button 
                variant="outline"
                onClick={() => downloadPDF(selectedRequest)}
                disabled={downloadingId === selectedRequest?.id}
                className="flex-1 sm:flex-none"
              >
                {downloadingId === selectedRequest?.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => deleteRequest(selectedRequest?.id)}
                disabled={deletingId === selectedRequest?.id}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-1 sm:flex-none"
              >
                {deletingId === selectedRequest?.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  const subject = encodeURIComponent(`Re: Quote Request ${selectedRequest?.request_number}`);
                  window.open(`mailto:${selectedRequest?.customer_email}?subject=${subject}`);
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Mail className="w-4 h-4 mr-2" /> Contact
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Installer Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Installer</DialogTitle>
            <DialogDescription>
              Select an installer to assign to this quote request
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {installers.length === 0 ? (
              <p className="text-neutral-500 text-center py-4">
                No installers available. Add installers in the Installers tab.
              </p>
            ) : (
              <div className="space-y-2">
                {installers.map((installer) => (
                  <button
                    key={installer.id}
                    onClick={() => assignInstaller(selectedRequest?.id, installer.id)}
                    disabled={updating}
                    className="w-full p-3 text-left border rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors flex items-center gap-3"
                  >
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: installer.color || '#3b82f6' }}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{installer.name}</p>
                      <p className="text-sm text-neutral-500">{installer.email}</p>
                    </div>
                    {installer.skills?.length > 0 && (
                      <div className="flex gap-1">
                        {installer.skills.slice(0, 2).map((skill, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-neutral-100 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteRequestsTab;
